import { NgModule, APP_INITIALIZER } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import { CommonModule } from '@angular/common';
import { HttpModule } from '@angular/http';
import { RouterModule } from '@angular/router';
import { LightboxModule } from 'angular2-lightbox';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { AgmCoreModule } from '@agm/core';

// Components
import { Footer1Component } from './base-components/shared/footer1/footer1.component';
import { Property1Component } from './base-components/shared/property1/property1.component';
import { ApartmentTypeComponent } from './base-components/shared/apartment-type/apartment-type.component';
import { Property2Component } from './base-components/shared/property2/property2.component';
import { FooterVauntComponent } from './base-components/shared/footer-vaunt/footer-vaunt.component';
import { FormErrorComponent } from './form-error/form-error.component';
import { ReCaptchaComponent } from './re-captcha.component';
import { OpenStreetMapComponent } from './osm/open-street-map.component';
import { GmapsInterfaceComponent } from './gmaps/gmaps-interface/gmaps-interface.component';
import { GoogleMapComponent } from './gmaps/google-map/google-map.component';
import { GoogleMapIframeComponent } from './gmaps/google-map/google-map-iframe.component';
import { LocationSearchInputComponent } from './gmaps/location-search-input/location-search-input.component';

// Base-components
import { AboutUsBaseComponent } from './base-components/about-us-base.component';
import { ContactBaseComponent } from './base-components/contact-base.component';
import { HomeBaseComponent } from './base-components/home-base.component';
import { PortfolioBaseComponent } from './base-components/portfolio-base.component';
import { ProjectDetailsBaseComponent } from './base-components/project-details-base.component';
import { HousesBaseComponent } from './base-components/houses-base.component';
import { ApartmentTypeDetailsBaseComponent } from './base-components/apartment-type-details-base.component';
import { ApartmentTypesBaseComponent } from './base-components/apartment-types-base.component';
import { PartnersBaseComponent } from './base-components/partners-base.component';
import { ServicesBaseComponent } from './base-components/services-base.component';

// shared base components
import { ShowcaseComponent } from './base-components/shared/showcase/showcase.component';
import { TouchGuideComponent } from './base-components/shared/map-plan/touch-guide.component';
import { PromoBoxComponent } from './base-components/shared/map-plan/promo-box.component';
import { MapPlanProjectBaseComponent } from './base-components/shared/map-plan/map-plan-project-base.component';
import { MapPlanBuildingBaseComponent } from './base-components/shared/map-plan/map-plan-building-base.component';
import { MapPlanProjectMobileBaseComponent } from './base-components/shared/map-plan/map-plan-project-mobile-base.component';
import { MapPlanBuildingFloorBaseComponent } from './base-components/shared/map-plan/map-plan-building-floor-base.component';
import { ContactDetailsBaseComponent } from './base-components/shared/contact-details-base/contact-details-base.component';
import { ContactFormBaseComponent } from './base-components/shared/contact-form-base/contact-form-base.component';
import { SocialIconsBaseComponent } from './base-components/shared/social-icons-base/social-icons-base.component';
import { TestimonialsCarouselBaseComponent } from './base-components/shared/testimonials-carousel-base.component';
import { SliderBaseComponent } from './base-components/shared/slider-base/slider-base.component';
import { ApartmentSearchBaseComponent } from './base-components/shared/apartment-search-base.component';
import { AdvancedSearchBaseComponent } from './base-components/shared/advanced-search-base.component';
import { HouseDetailsBaseComponent } from './base-components/house-details-base.component';
import { MenuContainerBaseComponent } from './base-components/shared/menu/menu-container-base/menu-container-base.component';
import { MenuItemBaseComponent } from './base-components/shared/menu/menu-item-base/menu-item-base.component';
import { ImagesCarouselBaseComponent } from './base-components/shared/images-carousel-base/images-carousel-base.component';
import {
  ShowContactFormModalBaseComponent
} from './base-components/shared/modals/show-contact-form-modal-base/show-contact-form-modal-base.component';
import {
  ShowItemDetailsModalBaseComponent
} from './base-components/shared/modals/show-item-details-modal-base/show-item-details-modal-base.component';
import {
  ShowTestimonialsContentModalBaseComponent
} from './base-components/shared/modals/show-testimonials-content-modal-base/show-testimonials-content-modal-base.component';
import {
  ShowCameraTutorialModalBaseComponent
} from './base-components/shared/modals/show-camera-tutorial-modal-base/show-camera-tutorial-modal-base.component';
import { LoadingSpinnerComponent } from './utils/loading-spinner/loading-spinner.component';
import { WhatsappComponent } from './utils/whatsapp/whatsapp.component';
import { ScrollGuideComponent } from './utils/scroll-guide/scroll-guide.component';
// import { CookieComponent } from './utils/cookie/cookie.component';

// Seo
import { JsonLdComponent } from './seo/json-ld.component';
import { HouseDetailsSdComponent } from './seo/house-details-sd.component';
import { OrganizationSdComponent } from './seo/organization-sd.component';

// Tooltips
import {
  TooltipsContainerComponent,
  TooltipBaseComponent,
  TooltipService,
  TooltipDirective
} from './base-components/shared/index';

// Dirctives
import { LightboxDirective } from './directives/lightbox.directive';
import { HoverScrollDirective } from './directives/hover-scroll.directive';
import { ClickStopPropagationDirective } from './directives/click-stop-propagation.directive';
import { GmapsDirectionsDirective } from './gmaps/gmaps-directions.directive';
import { GtagEventDirective } from './analytics/gtag-event.directive';
import { FbqEventDirective } from './analytics/fbq-event.directive';

// Services
import { BuildingFloorService } from './api/building-floor.service';
import { CameraService } from './api/camera.service';
import { LiveCameraService } from './api/live-camera.service';
import { BaseApiService } from './api/base-api.service';
import { AdvancedLimitsService } from './api/advanced-limits.service';
import { SimpleLimitsService } from './api/simple-limits.service';
import { ApiLoadingService } from './api/api-loading.service';
import { WebPageService } from './api/web-page.service';
import { ReasonService } from './api/reason.service';
import { ProjectService } from './api/project.service';
import { PartnerService } from './api/partner.service';
import { PropertyService } from './api/property.service';
import { ApartmentTypeService } from './api/apartment-type.service';
import { AgentService } from './api/agent.service';
import { ServiceService } from './api/service.service';
import { ContactService } from './api/contact.service';
import { TestimonialService } from './api/testimonial.service';
import { CityService } from './api/city.service';
import { AgencyService } from './api/agency.service';
import { ApiCallService } from './api/api-call.service';
import { OpenModalService } from './base-components/shared/modals/open-modal.service';
// import { CookieHeightChangeService } from './utils/cookie/cookie-height-change.service';
// import { CookieConsentService } from './utils/cookie/cookie-consent.service';
import { GtagService } from './analytics/gtag.service';
import { FbqService } from './analytics/fbq.service';
import { ListTrackingService } from './utils/list-tracking.service';
import { EmbedVideoService } from './utils/embed-video.service';

// Pipes
import { TruncatePipe } from './pipes/index';
import { BeautifyPhonePipe } from './pipes/beautify-phone.pipe';
import { ParseFloatPipe } from './pipes/parse-float.pipe';

// Material
import {
  MatInputModule,
  MatDialogModule,
  MatSelectModule,
  MatTooltipModule,
  MatButtonModule,
  MatSnackBarModule,
  MatIconModule,
  MatCheckboxModule
} from '@angular/material';

// Translate
import { TranslateModule } from '@ngx-translate/core';
import { TranslateLoader } from '@ngx-translate/core';
import { exportTranslateStaticLoader } from './i18n/translate-browser-loader.service';
import { TransferState } from '@angular/platform-browser';

import { LocaleService } from './seo/locale.service'

/* tslint:disable:only-arrow-functions */
export function getWindow(): any {
  return (typeof window !== 'undefined') ? window : null;
}

export function getScreen(): any {
  return (typeof screen !== 'undefined') ? screen : null;
}

export function agencyProviderFactory(provider: AgencyService): any {
  return () => provider.getAgency();
}
/* tslint:enable */

@NgModule({
  imports: [
    HttpModule,
    RouterModule,
    CommonModule,
    HttpClientModule,
    FormsModule,
    FlexLayoutModule,
    LightboxModule,
    AgmCoreModule.forRoot({
      apiKey: 'AIzaSyDqK0aUZbZtx31c8Ic6qQOCLctffxhG4Dk'
    }),
    TranslateModule.forChild({
      loader: {
        provide: TranslateLoader,
        useFactory: exportTranslateStaticLoader,
        deps: [HttpClient, TransferState]
      }
    }),
    // Material
    MatInputModule,
    MatDialogModule,
    MatSelectModule,
    MatTooltipModule,
    MatButtonModule,
    MatSnackBarModule,
    MatIconModule,
    MatCheckboxModule
  ],
  declarations: [
    // Components
    FormErrorComponent,
    ReCaptchaComponent,
    Property1Component,
    Property2Component,
    ApartmentTypeComponent,
    Footer1Component,
    TooltipsContainerComponent,
    JsonLdComponent,
    HouseDetailsSdComponent,
    OrganizationSdComponent,
    FooterVauntComponent,
    GmapsInterfaceComponent,
    OpenStreetMapComponent,
    GoogleMapComponent,
    GoogleMapIframeComponent,
    LocationSearchInputComponent,

    // Base-components
    AboutUsBaseComponent,
    ContactBaseComponent,
    HomeBaseComponent,
    HouseDetailsBaseComponent,
    HousesBaseComponent,
    ApartmentTypesBaseComponent,
    ApartmentTypeDetailsBaseComponent,
    PartnersBaseComponent,
    ServicesBaseComponent,
    ContactDetailsBaseComponent,
    MapPlanProjectBaseComponent,
    MapPlanBuildingBaseComponent,
    MapPlanProjectMobileBaseComponent,
    MapPlanBuildingFloorBaseComponent,
    TouchGuideComponent,
    ShowcaseComponent,
    PromoBoxComponent,
    ContactFormBaseComponent,
    SocialIconsBaseComponent,
    SliderBaseComponent,
    TestimonialsCarouselBaseComponent,
    ApartmentSearchBaseComponent,
    AdvancedSearchBaseComponent,
    ShowContactFormModalBaseComponent,
    ShowItemDetailsModalBaseComponent,
    ShowTestimonialsContentModalBaseComponent,
    ShowCameraTutorialModalBaseComponent,
    PortfolioBaseComponent,
    ProjectDetailsBaseComponent,
    TooltipBaseComponent,
    MenuContainerBaseComponent,
    MenuItemBaseComponent,
    ImagesCarouselBaseComponent,
    LoadingSpinnerComponent,
    WhatsappComponent,
    ScrollGuideComponent,
    // CookieComponent,

    // Directives
    TooltipDirective,
    ClickStopPropagationDirective,
    HoverScrollDirective,
    LightboxDirective,
    GmapsDirectionsDirective,
    GtagEventDirective,
    FbqEventDirective,

    // Pipes
    TruncatePipe,
    BeautifyPhonePipe,
    ParseFloatPipe
  ],
  exports: [
    TruncatePipe,
    BeautifyPhonePipe,
    ParseFloatPipe,
    FormErrorComponent,
    ReCaptchaComponent,
    SocialIconsBaseComponent,
    ContactFormBaseComponent,
    SliderBaseComponent,
    ContactDetailsBaseComponent,
    MapPlanProjectBaseComponent,
    MapPlanBuildingBaseComponent,
    MapPlanProjectMobileBaseComponent,
    MapPlanBuildingFloorBaseComponent,
    TouchGuideComponent,
    ShowcaseComponent,
    PromoBoxComponent,
    Property1Component,
    Property2Component,
    ApartmentTypeComponent,
    Footer1Component,
    TooltipsContainerComponent,
    TooltipDirective,
    ClickStopPropagationDirective,
    HoverScrollDirective,
    LightboxDirective,
    FooterVauntComponent,
    MenuContainerBaseComponent,
    MenuItemBaseComponent,
    JsonLdComponent,
    HouseDetailsSdComponent,
    OrganizationSdComponent,
    GmapsDirectionsDirective,
    ImagesCarouselBaseComponent,
    GtagEventDirective,
    FbqEventDirective,
    GmapsInterfaceComponent,
    OpenStreetMapComponent,
    GoogleMapComponent,
    GoogleMapIframeComponent,
    LoadingSpinnerComponent,
    WhatsappComponent,
    ScrollGuideComponent,
    LocationSearchInputComponent,
    // CookieComponent
  ],
  entryComponents: [
    ShowItemDetailsModalBaseComponent,
    ShowContactFormModalBaseComponent,
    ShowTestimonialsContentModalBaseComponent,
    ShowCameraTutorialModalBaseComponent
  ],
  providers: [
    ApiCallService,
    LocaleService,
    AgencyService,
    CityService,
    TestimonialService,
    ContactService,
    ServiceService,
    AgentService,
    PropertyService,
    ApartmentTypeService,
    PartnerService,
    ProjectService,
    ReasonService,
    WebPageService,
    ApiLoadingService,
    SimpleLimitsService,
    AdvancedLimitsService,
    BaseApiService,
    LiveCameraService,
    CameraService,
    BuildingFloorService,
    OpenModalService,
    TooltipService,
    // CookieHeightChangeService,
    // CookieConsentService,
    ListTrackingService,
    EmbedVideoService,
    GtagService,
    FbqService,
    { provide: 'WINDOW', useFactory: getWindow },
    { provide: 'SCREEN', useFactory: getScreen },
    { provide: APP_INITIALIZER, useFactory: agencyProviderFactory, deps: [AgencyService], multi: true }
  ]
})
export class CommonThemeModule { }



// WEBPACK FOOTER //
// ./src/app/common-theme/common-theme.module.ts